import { ColorId, DirectionId, colorIds } from '../defs/block';

export type CountSize = {
  count: number;
  width: number;
  height: number;
};

function numDigits(x: number) {
  return Math.max(Math.floor(Math.log10(Math.abs(x))), 0) + 1;
}

function clamp(x: number, min: number, max: number) {
  return Math.max(min, Math.min(max, x));
}

const directions = ['up', 'right', 'down', 'left'] as const;

export type CountDirection = {
  count: number;
  direction: DirectionId;
};

/**
 * Convert count, width and height into a single number.
 * Ex.: count 15, width 5, height 3 turns into 150503.
 */
export function blockOptionFromCountSize(args: CountSize): number {
  // Clamp values to positive 2 digits
  const count = Math.max(0, Math.min(99, args.count));
  const width = Math.max(0, Math.min(99, args.width));
  const height = Math.max(0, Math.min(99, args.height));

  // Calculate each component
  const a = count * Math.pow(10, 4);
  const b = width * Math.pow(10, 2);
  const c = height;

  // Return the sum
  return a + b + c;
}

/**
 * Convert a single number into count, width and height.
 * Ex.: 150503 turns into count 15, width 5, height 3.
 */
export function blockOptionToCountSize(option: number): CountSize | null {
  // Only allow positive values
  if (option <= 0) return null;

  // Check if the number has at least 5 digits
  if (numDigits(option) < 5) return null;

  // Extract each component
  const a = Math.floor(option / Math.pow(10, 4));
  const b = Math.floor((option % Math.pow(10, 4)) / Math.pow(10, 2));
  const c = Math.floor(option % Math.pow(10, 2));

  // Return the values as a CountSize object
  return {
    count: a,
    width: b,
    height: c,
  };
}

/**
 * Convert count and direction into a single number.
 * Ex.: count 15, direction 2 (down) turns into 152.
 */
export function blockOptionFromCountDirection(args: CountDirection): number {
  const directionIndex = directions.indexOf(args.direction);

  // Clamp values
  const count = clamp(args.count, 0, 99);
  const direction = clamp(directionIndex, 0, 3);

  // Calculate each component
  const a = count * Math.pow(10, 1);
  const b = direction;

  // Return the sum
  return a + b;
}

/**
 * Convert a single number into count and direction.
 * Ex.: 152 turns into count 15 and direction 2 (down).
 */
export function blockOptionToCountDirection(
  option: number,
): CountDirection | null {
  // Only allow positive values
  if (option <= 0) return null;

  // Check if the number has at least 5 digits
  if (numDigits(option) < 2) return null;

  // Extract each component
  const a = Math.floor(option / Math.pow(10, 1));
  const b = Math.floor(option % Math.pow(10, 1));
  const directionIndex = clamp(b, 0, 3);

  // Return the values as a CountSize object
  return {
    count: a,
    direction: directions[directionIndex],
  };
}

export type CountColor = {
  count: number;
  color: ColorId;
};

/**
 * Convert count and color into a single number.
 * Ex.: count 15, color 2 turns into 152.
 */
export function blockOptionFromCountColor(args: CountColor): number {
  const colorIndex = colorIds.indexOf(args.color);
  const count = clamp(args.count, 1, 99);
  const color = clamp(colorIndex, 0, colorIds.length - 1);

  // Calculate each component
  const a = count * 10;
  const b = color;

  // Return the sum
  return a + b;
}

/**
 * Convert a single number into count and color.
 * Ex.: 152 turns into count 15 and color 'green'.
 */
export function blockOptionToCountColor(option: number): CountColor | null {
  // Only allow positive values
  if (option <= 0) return null;

  // Check if the number has 3 digits
  if (numDigits(option) < 2) return null;

  // Extract each component
  const a = Math.floor(option / 10);
  const b = Math.floor(option % 10);
  const colorIndex = clamp(b, 0, colorIds.length - 1);

  // Return the values as a CountSize object
  return {
    count: a,
    color: colorIds[colorIndex],
  };
}
